// "Write the email" on a Today row (Marketing Hub 2.3b-ii): which rows offer
// it, which editable template a row starts from, the merge fields, and how
// the typed text becomes the HTML Outlook sends.
//
// CONSUMER: shared (the Today email route, the Today page, tests).
//
// WHY THE WORDING IS DATA, NOT A STRING LITERAL. CLAUDE.md, automation-first
// rule 2: "Message wording never lives in code." Each row resolves against
// `communication_templates` by a well-known slug (seeded by migration
// 20261001223627 so they show in the template editor). The FALLBACKS below
// apply only until that row is missing; the moment one exists it wins, and
// anyone with the template editor can rewrite it with no deploy.
//
// WHAT AN EMAIL NEVER CARRIES. The OS's reason for the row (the sentence on
// the page, its sources, the chance) stays inside the building: it is
// internal-only judgment, not something to quote at a family. A life event
// is never named, because the OS cannot know whether it is a happy one. No
// health information is read here at all.
//
// No database and no React, so the server and the page can never disagree.

import { renderTemplate } from "../communications/template-merge";
import type { DecisionAction, MomentKind } from "./decision-vocabulary";

/** The suggested steps that are a message to the family. "Ask the one
 *  question" is a message too, but with answer links (one-question.ts), so it
 *  is offered by rowAsksQuestion, not here. */
export const EMAIL_ACTIONS: readonly DecisionAction[] = [
  "draft_personal_message",
  "send_education",
  "invite_to_review",
  "ask_review_or_introduction",
];

/**
 * Row 4.0: the moment whose message is the advisor's thank-you (row 4.3,
 * modal 28's own email path, never the generic draft). Row 4.5 wired the
 * introduction ask's words, so a favorable review (public_review) offers
 * the email again, from the approved template.
 */
export const EMAIL_WITHHELD_MOMENTS: readonly MomentKind[] = [
  "referral_given",
  // Row 10.2: a second lien and a listing are a task for the advisor, with
  // no draft (Grant, 2026-10-24).
  "new_lien",
  "just_listed",
  // Row 10.3: after a loss the advisor calls; nothing is drafted.
  "after_loss",
  // Long-Term Care age: a task for the advisor, a call rather than a draft.
  "ltc_age",
];

export function rowOffersEmail(action: string, momentKind?: string | null): boolean {
  if (momentKind && (EMAIL_WITHHELD_MOMENTS as readonly string[]).includes(momentKind)) return false;
  return (EMAIL_ACTIONS as readonly string[]).includes(action);
}

export type TodayEmailTemplateKey = MomentKind | "ask_review" | "one_question" | "general";

export interface TodayEmailFallback {
  slug: string;
  subject: string;
  body: string;
}

/**
 * The starting wording for each kind of row. Plain text, short, written the
 * way an advisor would write to a family they know. Merge fields:
 * {{first_name}} (the person it is addressed to), {{product}} (what the row
 * is about, in lower case), {{company_name}} ("Stewardship Insurance") and
 * {{sender_first_name}} (whoever presses the button).
 */
export const TODAY_EMAIL_FALLBACKS: Record<TodayEmailTemplateKey, TodayEmailFallback> = {
  renewal_window: {
    slug: "nbh-email-renewal-window",
    subject: "Your {{product}} renews soon",
    body:
      "Hi {{first_name}},\n\nYour {{product}} comes up for renewal soon, and I wanted to reach out before it does. Prices and coverage can shift from one year to the next, so this is a good moment to make sure it still fits your family.\n\nDo you have 15 minutes for a quick call this week or next? If everything still looks right, we leave it alone.\n\n{{sender_first_name}}",
  },
  funded_no_home_policy: {
    slug: "nbh-email-new-home",
    subject: "Congratulations on the new home",
    body:
      "Hi {{first_name}},\n\nCongratulations again on the new home. I wanted to check one thing while it is fresh: we don't have your home insurance on file with us. If you'd like a second set of eyes on what you have, I'm happy to compare it for you. No pressure either way.\n\n{{sender_first_name}}",
  },
  turning_65: {
    slug: "nbh-email-turning-65",
    subject: "A big birthday is coming up",
    body:
      "Hi {{first_name}},\n\nA big birthday is on the way. Turning 65 comes with a few decisions that have deadlines, and Medicare is the big one. I'd love to walk through it with you before the window opens so nothing sneaks up on you.\n\nWould a short call in the next few weeks work?\n\n{{sender_first_name}}",
  },
  revisit_due: {
    slug: "nbh-email-revisit-due",
    subject: "Checking back in",
    body:
      "Hi {{first_name}},\n\nA while back the timing wasn't right to talk about your {{product}}, and I said I'd check back. So here I am. If now still isn't the time, just say so and I'll leave it be.\n\n{{sender_first_name}}",
  },
  interested: {
    slug: "nbh-email-interested",
    subject: "Following up on your {{product}}",
    body:
      "Hi {{first_name}},\n\nYou mentioned you were interested in talking about your {{product}}, and I didn't want that to slip. When is a good time for a quick call?\n\n{{sender_first_name}}",
  },
  long_silence: {
    slug: "nbh-email-long-silence",
    subject: "It has been a while",
    body:
      "Hi {{first_name}},\n\nI realized it has been a while since we caught up, and I wanted to check in. How is the family? If anything has changed or there is something on your mind, I'm one reply away.\n\n{{sender_first_name}}",
  },
  review_due: {
    slug: "nbh-email-review-due",
    subject: "Time for your review",
    body:
      "Hi {{first_name}},\n\nIt's that time of year for your review with {{company_name}}. It is a short conversation to make sure everything still matches where your family is today.\n\nWhat does your calendar look like over the next couple of weeks?\n\n{{sender_first_name}}",
  },
  life_event: {
    slug: "nbh-email-life-event",
    subject: "Thinking of you",
    body:
      "Hi {{first_name}},\n\nYou came to mind today and I wanted to check in. If anything has changed for your family and you'd like to talk it through, I'm here.\n\n{{sender_first_name}}",
  },
  // Row 4.0. Plain starting words for the service moments; each is a
  // template an admin rewrites under Templates. The thank-you (row 4.3) and
  // the introduction ask (row 4.5) arrive with their approved words and
  // are withheld until then (EMAIL_WITHHELD_MOMENTS); their entries here
  // exist so the key set stays complete and are never offered.
  referral_received: {
    slug: "nbh-email-referral-received",
    subject: "Following up on your introduction to {{company_name}}",
    body: "Hi {{first_name}},\n\nSomeone you know thought we might be helpful and passed your name along. I wanted to reach out myself rather than leave it at that. Would a quick call this week suit you?\n\n{{sender_first_name}}",
  },
  referral_given: {
    slug: "nbh-email-referral-given",
    subject: "Thank you",
    body: "Hi {{first_name}},\n\nThank you for thinking of us. It means a great deal.\n\n{{sender_first_name}}",
  },
  public_review: {
    slug: "nbh-email-public-review",
    subject: "Thank you for the kind words",
    body: "Hi {{first_name}},\n\nThank you for taking the time to leave a review. It made our week.\n\n{{sender_first_name}}",
  },
  review_concern: {
    slug: "nbh-email-review-concern",
    subject: "I read your review and I want to make it right",
    body: "Hi {{first_name}},\n\nI read what you wrote and I am sorry we fell short. I would like to hear more and put it right. Could I call you this week?\n\n{{sender_first_name}}",
  },
  first_anniversary: {
    slug: "nbh-email-first-anniversary",
    subject: "A year already",
    body: "Hi {{first_name}},\n\nIt has been a year since your {{product}} with {{company_name}}. I hope the year has been good to you. If anything has changed, or you have a question I can help with, I am a reply away.\n\n{{sender_first_name}}",
  },
  claim_closed: {
    slug: "nbh-email-claim-closed",
    subject: "Now that the claim is closed",
    body: "Hi {{first_name}},\n\nI saw the claim on your {{product}} has closed. I hope it went as smoothly as it could. If anything about how it was handled still sits wrong, or you want to talk through what it means for your coverage, I am here.\n\n{{sender_first_name}}",
  },
  endorsement: {
    slug: "nbh-email-endorsement",
    subject: "A quick check on the change to your policy",
    body: "Hi {{first_name}},\n\nI saw the recent change on your {{product}}. I wanted to make sure everything looks right to you and that the coverage still fits. Happy to walk through it if that would help.\n\n{{sender_first_name}}",
  },
  address_change: {
    slug: "nbh-email-address-change",
    subject: "Your new address",
    body: "Hi {{first_name}},\n\nThank you for sending us your new address. I have made a note on your file. If the move changes anything we look after for you, let me know and we will sort it out.\n\n{{sender_first_name}}",
  },
  note_rate: {
    slug: "nbh-email-note-rate",
    subject: "A look at your rate",
    body: "Hi {{first_name}},\n\nRates have moved since your loan closed, and yours may be worth a look. No pressure at all; if you would like, I can run the numbers and tell you plainly whether a change makes sense.\n\n{{sender_first_name}}",
  },
  // Row 4.4: the review ask and its one follow-up (client piece 50). The
  // private path comes first; the buttons are built by the server from
  // per-person links (review-ask.ts) and go where the two markers sit.
  // Both rows send only once Grant approves the wording (the stamp on the
  // template row, S4-7); the fallback here is never offered for sending.
  file_completed: {
    slug: "nbh-email-file-completed",
    subject: "A small favor",
    body:
      "Hi {{first_name}},\n\nWe closed your file {{closed_day}}. If anything about it was not right, tell me first and we will fix it:\n\n{{concern_button}}\n\nIf it went the way we hoped, a review helps the next family find us. I wrote nothing for you to copy; say what you would say to a neighbor.\n\n{{review_buttons}}\n\nOne follow-up in five days if you have not, then never again for this file. There is no reward for a review, ever.\n\n{{sender_first_name}}",
  },
  review_follow_up: {
    slug: "nbh-email-review-follow-up",
    subject: "One more time, then I will leave it",
    body:
      "Hi {{first_name}},\n\nA quick follow-up to my note from a few days ago. If anything about your file was not right, tell me first:\n\n{{concern_button}}\n\nIf it went well and you have two minutes, a review helps the next family find us.\n\n{{review_buttons}}\n\nThis is the one follow-up; you will not hear about it again for this file. There is no reward for a review, ever.\n\n{{sender_first_name}}",
  },
  // Row 4.5: the introduction ask (client piece 51), two to three weeks
  // after a completed file, and the words a favorable review sends. The
  // buttons are built by the server from per-person links
  // (introduction-ask.ts); {{mention_line}} is the family's own check-in
  // words when they named someone. Sends only once Grant approves it.
  introduction_due: {
    slug: "nbh-email-introduction-due",
    subject: "A small ask, and no pressure",
    body:
      "Hi {{first_name}},\n\n{{mention_line}}\n\nIf someone you know would like a no-pressure conversation with {{company_name}}, I would be glad to have it. You can introduce us with the button below, copy them on this note, or send them my number.\n\n{{introduce_buttons}}\n\nNo obligation, and if the timing is wrong, no follow-up. If you do introduce someone, the only thing you will ever get from us is a thank-you note; that is the rule in our business, and it is how we want it.\n\n{{sender_first_name}}",
  },
  // Row 10.2: the county-record moments. A public record is timing, never
  // named to the family (S10-8): these words never mention the record, the
  // sale, the loan or the lender, and the send route refuses a note that does
  // (public-record-moments.ts). The second lien and the listing are tasks for
  // the advisor with no draft (EMAIL_WITHHELD_MOMENTS); their entries keep
  // the key set complete and are never offered.
  home_sold: {
    slug: "nbh-email-home-sold",
    subject: "Thinking of you and your plans",
    body:
      "Hi {{first_name}},\n\nYou came to mind today and I wanted to check in. If anything has changed with where your family is living, or is about to, I would love to help make sure everything that should follow you does, from your coverage to whatever comes next.\n\nIs there a good time for a quick call this week?\n\n{{sender_first_name}}",
  },
  refinanced_elsewhere: {
    slug: "nbh-email-refinanced-elsewhere",
    subject: "Checking in",
    body:
      "Hi {{first_name}},\n\nI was thinking about you and wanted to check in. Rates and options keep moving, and I am always glad to take a second look at what you have today and make sure it still serves your family well. No pressure at all.\n\nIs there a good time to catch up?\n\n{{sender_first_name}}",
  },
  // The dated foreclosure moment (Grant, 2026-10-29): a draft for the advisor
  // only. These words offer a conversation and never name the notice, the
  // lender, the loan or the date; the send route refuses a note that names
  // the record (public-record-moments.ts).
  foreclosure_sale: {
    slug: "nbh-email-foreclosure-sale",
    subject: "Checking in",
    body:
      "Hi {{first_name}},\n\nYou have been on my mind and I wanted to reach out. If there is ever anything about your home or your finances you would like another set of eyes on, I would be glad to talk it through. No pressure and no obligation, just a conversation.\n\nIs there a good time for a call this week?\n\n{{sender_first_name}}",
  },
  new_lien: {
    slug: "nbh-email-new-lien",
    subject: "Checking in",
    body: "Hi {{first_name}},\n\nI wanted to check in and see how things are going.\n\n{{sender_first_name}}",
  },
  just_listed: {
    slug: "nbh-email-just-listed",
    subject: "Checking in",
    body: "Hi {{first_name}},\n\nI wanted to check in and see how things are going.\n\n{{sender_first_name}}",
  },
  after_loss: {
    slug: "nbh-email-after-loss",
    subject: "Checking in",
    body: "Hi {{first_name}},\n\nI wanted to check in and see how things are going.\n\n{{sender_first_name}}",
  },
  ltc_age: {
    slug: "nbh-email-ltc-age",
    subject: "Checking in",
    body: "Hi {{first_name}},\n\nI wanted to check in and see how things are going.\n\n{{sender_first_name}}",
  },
  ask_review: {
    slug: "nbh-email-ask-review",
    subject: "A small favor",
    body:
      "Hi {{first_name}},\n\nIt has been a real joy working with your family. If you have two minutes, a short review of {{company_name}} would mean a lot to us. And if you know someone who could use the same kind of help, I'd be glad to meet them.\n\nThank you either way.\n\n{{sender_first_name}}",
  },
  one_question: {
    slug: "nbh-email-one-question",
    subject: "A quick question about your {{product}}",
    body:
      "Hi {{first_name}},\n\nA quick question so we keep your file right, about {{product}}: which of these fits your family today?\n\n{{answer_buttons}}\n\nOne tap is all it takes. No pitch, I promise.\n\n{{sender_first_name}}",
  },
  general: {
    slug: "nbh-email-general",
    subject: "Checking in",
    body:
      "Hi {{first_name}},\n\nI wanted to check in and see how things are going. If there is anything I can help with, just reply here.\n\n{{sender_first_name}}",
  },
};

/** Which template a row starts from: the ask for a review is about the
 *  action, everything else about the moment the OS noticed. */
export function templateKeyFor(action: string, momentKind: string | null): TodayEmailTemplateKey {
  // Row 4.4: the review ask and its follow-up are about the file, and their
  // words are the two approved templates, never the generic ask.
  if (momentKind === "file_completed" || momentKind === "review_follow_up") return momentKind;
  // Row 4.5: the introduction ask's words, for the file two weeks on and for
  // the favorable review alike.
  if (momentKind === "introduction_due" || momentKind === "public_review") return "introduction_due";
  if (action === "ask_review_or_introduction") return "ask_review";
  if (action === "ask_one_question") return "one_question";
  if (action === "invite_to_review") return "review_due";
  if (momentKind && Object.prototype.hasOwnProperty.call(TODAY_EMAIL_FALLBACKS, momentKind)) {
    return momentKind as MomentKind;
  }
  return "general";
}

const KEEP_CASE = new Set(["Medicare"]);

/** "Home Policy" to "home policy", keeping "HELOC", "RV" and "Medicare". */
export function productWord(label: string | null): string {
  if (!label) return "coverage";
  return label
    .split(" ")
    .map((w) => (KEEP_CASE.has(w) || /^[A-Z]{2,}[^A-Za-z]*$/.test(w) ? w : w.toLowerCase()))
    .join(" ");
}

/** The first word of a name, or "there" ("Hi there,") when none is on file. */
export function firstNameOf(name: string | null | undefined): string {
  const first = (name ?? "").trim().split(/\s+/)[0] ?? "";
  return first.length > 0 ? first : "there";
}

export interface TodayEmailMerge {
  first_name: string;
  product: string;
  company_name: string;
  sender_first_name: string;
  /** Row 4.4: "Friday", "yesterday", "on Oct 3, 2026"; the review ask's
   *  file. Absent on every other row (the field then renders empty). */
  closed_day?: string;
  /** Row 4.5: the introduction ask's opening line (mentionLine). */
  mention_line?: string;
}

/** Subject and body from the stored template, else the fallback. */
export function composeTodayEmail(
  key: TodayEmailTemplateKey,
  stored: { subject_template: string; body_template: string } | null,
  merge: TodayEmailMerge,
): { subject: string; body: string; slug: string; from_template: boolean } {
  const fb = TODAY_EMAIL_FALLBACKS[key];
  const fields = { ...merge };
  return {
    subject: renderTemplate(stored?.subject_template ?? fb.subject, fields),
    body: renderTemplate(stored?.body_template ?? fb.body, fields),
    slug: fb.slug,
    from_template: stored !== null,
  };
}

function escapeHtml(s: string): string {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;").replace(/'/g, "&#39;");
}

/**
 * The typed plain text as HTML: every character escaped (a family's email
 * must never carry markup someone typed by accident), blank lines become
 * paragraphs, single line breaks stay line breaks. The person's saved
 * signature, already cleaned when it was saved, goes underneath.
 *
 * `answerButtons` (the one question's links, built by the server, never
 * typed) replaces a paragraph holding only {{answer_buttons}}; if the person
 * took that marker out, the buttons go under the message, above the
 * signature. A marker typed mid-sentence is left as text: the buttons must
 * never land somewhere the person did not put them on their own line.
 */
export function todayEmailHtml(
  text: string,
  signatureHtml: string | null,
  answerButtons: string | null = null,
  /** Row 4.4: more server-built blocks by their marker ({{concern_button}},
   *  {{review_buttons}}), placed the same way as the answer buttons. */
  namedBlocks: Readonly<Record<string, string>> = {},
): string {
  const blocks = text
    .replace(/\r\n/g, "\n")
    .split(/\n{2,}/)
    .map((p) => p.trim())
    .filter((p) => p.length > 0);
  let placed = false;
  const placedNamed = new Set<string>();
  const paragraphs = blocks.map((p) => {
    if (answerButtons !== null && !placed && p === "{{answer_buttons}}") {
      placed = true;
      return answerButtons;
    }
    if (Object.prototype.hasOwnProperty.call(namedBlocks, p) && !placedNamed.has(p)) {
      placedNamed.add(p);
      return namedBlocks[p]!;
    }
    return `<p>${escapeHtml(p).replace(/\n/g, "<br>")}</p>`;
  });
  if (answerButtons !== null && !placed) paragraphs.push(answerButtons);
  for (const [marker, html] of Object.entries(namedBlocks)) if (!placedNamed.has(marker)) paragraphs.push(html);
  const sig = signatureHtml && signatureHtml.trim().length > 0 ? `<div>${signatureHtml}</div>` : "";
  return `${paragraphs.join("")}${sig}`;
}

/**
 * Who each outgoing message is to. A row that asks the one question sends
 * one message PER PERSON, because the answer buttons are a link bound to one
 * contact: a shared message would record a spouse's tap as the first
 * person's, and the link's opt-out check would read only that one person.
 * Every other row is one message to everyone ticked, as before.
 *
 * DECISION: one message each over one message with one shared link. sendMail
 * takes one body, so per-person buttons cannot ride in a shared message, and
 * a shared link is exactly the bug. The cost is that the words are the same
 * in each copy (a "Hi Maria," greeting reaches the spouse as typed); the OS
 * never rewrites what an advisor wrote.
 */
export function messageRecipients<T>(recipients: readonly T[], asksQuestion: boolean): T[][] {
  return asksQuestion ? recipients.map((r) => [r]) : recipients.length > 0 ? [[...recipients]] : [];
}

/** "A and B", "A, B and C": names as an advisor would say them. */
export function joinNames(names: readonly string[]): string {
  if (names.length <= 1) return names.join("");
  return `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
}

/** The refusal when someone ticked is on the bounce / complaint list. */
export function suppressedRefusalMessage(names: readonly string[]): string {
  return `${joinNames(names)} cannot be emailed right now: an email to that address bounced, or it was reported as spam or unsubscribed. Take them off and try again, or call instead.`;
}

/** The refusal when a marketing row's family has been paused since the row
 *  was computed. `reason` is the pause rule's own sentence. */
export function pausedRefusalMessage(reason: string): string {
  return `${reason} Marketing to this family is on hold, so this email is not sent. Refresh Today; the row will have moved.`;
}

/** The longest subject and body the route accepts. */
export const TODAY_EMAIL_SUBJECT_MAX = 200;
export const TODAY_EMAIL_BODY_MAX = 10_000;
/** A household email goes to a few people at most. */
export const TODAY_EMAIL_MAX_RECIPIENTS = 6;

export type TodayEmailMode = "draft" | "send";

/** What the composer opens with (GET), one type for server and page. */
export interface TodayEmailComposer {
  outlook_connected: boolean;
  /** Set when this person cannot email from here at all, with why. Row
   *  4.4: "unapproved" when the row's wording awaits Grant's stamp;
   *  `can_approve` then says whether the viewer may give it. */
  blocked: { reason: "impersonating" | "role" | "unapproved"; message: string; can_approve?: boolean; template_slug?: string } | null;
  recipients: Array<{
    contact_id: string;
    name: string;
    email: string;
    is_primary: boolean;
    /** Program audit PR 1: on a relational row, the soft stops the advisor
     *  should see before pressing (never a refusal). */
    notes?: string[];
  }>;
  /** Household members who cannot be emailed, with the reason in words. */
  not_emailable: Array<{
    contact_id: string;
    name: string;
    /** "refused": the one press-time rule's other reasons (program audit
     *  PR 1); `sentence` then says which, in plain words. */
    reason: "opted_out" | "suppressed" | "no_email" | "refused";
    sentence?: string;
  }>;
  default_contact_ids: string[];
  subject: string;
  body: string;
  template_slug: string;
  from_template: boolean;
  has_signature: boolean;
  /** Set on an "Ask the one question" row: the answers the email will carry. */
  question: { product: string; choices: { key: string; label: string }[] } | null;
  /** Row 4.4: set on a review-ask row. The buttons the email carries, where
   *  "Leave a Google review" goes (null when the company has no link on
   *  file: the email then carries the private path and "Not now" only),
   *  and whether this is the one follow-up. */
  review_ask?: { google_review_url: string | null; follow_up: boolean } | null;
  /** Row 4.5: set on an introduction-ask row: whether the family's own
   *  check-in words open the note, and which moment sends it. */
  introduction_ask?: { has_mention: boolean; from_review: boolean } | null;
}

/**
 * The composer's line for someone it may not write to (program audit PR 1):
 * the one press-time rule's own sentence when it gave one, else the three
 * reasons the composer always worded. One function for every Hub composer
 * (Today, the thank-you, Ask one question), so they can never word it two
 * ways.
 */
export function notEmailableSentence(p: { name: string; reason: "opted_out" | "suppressed" | "no_email" | "refused"; sentence?: string }): string {
  if (p.sentence && p.reason === "refused") return p.sentence;
  if (p.reason === "opted_out") return `${p.name} asked not to be emailed.`;
  if (p.reason === "suppressed") return `${p.name} cannot be emailed right now: that address bounced or was reported as spam.`;
  if (p.reason === "no_email") return `No email is on file for ${p.name}.`;
  return p.sentence ?? `${p.name} cannot be emailed from here right now.`;
}
